'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';

const SLIDES = [
{
  url: "https://img.rocket.new/generatedImages/rocket_gen_img_1290abdc4-1772152937372.png",
  alt: 'High-fashion editorial model in structured black coat on minimalist white background'
},
{
  url: "https://img.rocket.new/generatedImages/rocket_gen_img_173d78150-1772128683291.png",
  alt: 'Luxury runway editorial with draped ivory fabric and dramatic lighting'
},
{
  url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e8c24474-1772138558579.png",
  alt: 'Editorial fashion photography with model in avant-garde silhouette against neutral backdrop'
},
{
  url: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa10b51f-1772307255206.png",
  alt: 'High-end fashion editorial with model in monochrome ensemble, clean studio aesthetic'
},
{
  url: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3a592a5-1772621929808.png",
  alt: 'Luxury fashion editorial with model carrying designer accessories in urban setting'
}];

const INTERVAL_MS = 5000;

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [loaded, setLoaded] = useState<boolean[]>(new Array(SLIDES.length).fill(false));
  const [mounted, setMounted] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  // Trigger fade-in on mount
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = sectionRef.current.offsetHeight;
      // Only apply when section is in view
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        const scrolled = -rect.top;
        const parallax = scrolled * 0.35;
        setParallaxY(parallax);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (transitioning || index === current) return;
      setTransitioning(true);
      setTimeout(() => {
        setCurrent(index);
        setTransitioning(false);
      }, 600);
    },
    [current, transitioning]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      const next = (current + 1) % SLIDES.length;
      goTo(next);
    }, INTERVAL_MS);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const handleImageLoad = (index: number) => {
    setLoaded((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
  };

  return (
    <section ref={sectionRef} className="relative w-full overflow-hidden" style={{ height: 'clamp(480px, 72vh, 720px)' }}>
      {/* Slides with parallax */}
      {SLIDES.map((slide, i) =>
      <div
        key={i}
        className="absolute inset-0 transition-opacity duration-[1200ms] ease-in-out"
        style={{ opacity: i === current ? transitioning ? 0 : 1 : 0, zIndex: i === current ? 1 : 0 }}>
        
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
          src={slide.url}
          alt={slide.alt}
          onLoad={() => handleImageLoad(i)}
          className="w-full h-full object-cover object-top"
          style={{
            filter: 'grayscale(18%) contrast(1.04)',
            transform: `translateY(${parallaxY}px) scale(1.15)`,
            transformOrigin: 'center top',
            willChange: 'transform',
            transition: 'transform 0.05s linear'
          }}
          loading={i === 0 ? 'eager' : 'lazy'} />
        
        </div>
      )}

      {/* Gradient overlay — bottom-heavy for text readability */}
      <div
        className="absolute inset-0 z-10"
        style={{
          background:
          'linear-gradient(to bottom, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.18) 30%, rgba(0,0,0,0.52) 65%, rgba(0,0,0,0.82) 100%)'
        }} />

      {/* Subtle top vignette */}
      <div
        className="absolute inset-x-0 top-0 h-32 z-10"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.35), transparent)' }} />

      {/* Content */}
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-end pb-14 sm:pb-16 px-6 text-center">
        {/* Eyebrow label — fade in first */}
        <p
          className="mb-4 tracking-[0.28em] text-xs uppercase text-white/60 font-light"
          style={{
            letterSpacing: '0.28em',
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(18px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s'
          }}>
          Editorial Platform
        </p>

        {/* Main headline — fade in with slight delay */}
        <h1
          className="text-white font-normal mb-3 leading-none"
          style={{
            fontFamily: "'Cinzel', 'Didot', 'Bodoni MT', serif",
            fontSize: 'clamp(4rem, 11vw, 9rem)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.9s ease 0.3s, transform 0.9s ease 0.3s'
          }}>
          Silhouette
        </h1>

        {/* Subheadline */}
        <p
          className="text-white/70 font-light mb-4"
          style={{
            fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
            fontSize: 'clamp(0.85rem, 2vw, 1.05rem)',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(18px)',
            transition: 'opacity 0.8s ease 0.55s, transform 0.8s ease 0.55s'
          }}>
          Where fashion voices take shape
        </p>

        {/* Thin divider */}
        <div
          className="w-10 h-px bg-white/30 mb-5"
          style={{
            opacity: mounted ? 1 : 0,
            transition: 'opacity 0.7s ease 0.7s'
          }} />

        {/* Description */}
        <p
          className="text-white/45 max-w-sm sm:max-w-md mb-8 leading-relaxed font-light"
          style={{
            fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
            fontSize: 'clamp(0.75rem, 1.4vw, 0.85rem)',
            letterSpacing: '0.06em',
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(14px)',
            transition: 'opacity 0.8s ease 0.8s, transform 0.8s ease 0.8s'
          }}>
          A community for editorial perspectives, curated looks, and fashion-forward expression
        </p>

        {/* CTA Buttons — fade in last, with hover lift */}
        <div
          className="flex flex-col sm:flex-row items-center gap-3"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(14px)',
            transition: 'opacity 0.8s ease 1s, transform 0.8s ease 1s'
          }}>
          <Link
            href="#runway-feed"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('runway-feed')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-7 py-3 bg-white text-black text-sm font-medium tracking-widest uppercase active:scale-[0.97]"
            style={{
              fontFamily: "'Cinzel', serif",
              letterSpacing: '0.14em',
              minWidth: '160px',
              transition: 'transform 0.25s cubic-bezier(0.34,1.56,0.64,1), background 0.3s ease, box-shadow 0.25s ease'
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-4px)';
              (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 12px 28px rgba(255,255,255,0.18)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
              (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
            }}>
            Explore Runway
          </Link>
          <Link
            href="/sign-up-login-screen"
            className="px-7 py-3 border border-white/50 text-white text-sm font-light tracking-widest uppercase active:scale-[0.97]"
            style={{
              fontFamily: "'Cinzel', serif",
              letterSpacing: '0.14em',
              minWidth: '160px',
              transition: 'transform 0.25s cubic-bezier(0.34,1.56,0.64,1), border-color 0.3s ease, background 0.3s ease, box-shadow 0.25s ease'
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-4px)';
              (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,1)';
              (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.08)';
              (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 12px 28px rgba(255,255,255,0.10)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
              (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.5)';
              (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
              (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
            }}>
            Join Silhouette
          </Link>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {SLIDES.map((_, i) =>
        <button
          key={i}
          onClick={() => goTo(i)}
          aria-label={`Go to slide ${i + 1}`}
          className="transition-all duration-500"
          style={{
            width: i === current ? '24px' : '6px',
            height: '2px',
            background: i === current ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.35)',
            border: 'none',
            padding: 0,
            cursor: 'pointer'
          }} />
        )}
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 h-px bg-white/10">
        <div
          key={current}
          className="h-full bg-white/40"
          style={{
            animation: `heroProgress ${INTERVAL_MS}ms linear forwards`
          }} />
      </div>

      <style jsx>{`
        @keyframes heroProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>);

}